// ReporteDiarioCoordinadora.js
// 5.1 — Reporte diario de coordinadoras (rol supervisora).
// Corto, obligatorio, se entrega antes de las 17:45.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/authContext";

const HORA_LIMITE = "17:45";

const AYUDA_OPCIONES = [
  { key: "atencion_cliente", label: "Atención al cliente", emoji: "📞" },
  { key: "asistentas", label: "Asistentas", emoji: "🧹" },
  { key: "almacen", label: "Almacén", emoji: "📦" },
  { key: "direccion", label: "Dirección", emoji: "🏢" },
];

const CAMPOS = [
  { key: "pisos_visitados", label: "Pisos visitados", emoji: "🏠", ph: "Ej: SF32.6D, AB1.10B, IA20.1E…", area: true },
  { key: "incidencias_nuevas", label: "Incidencias nuevas", emoji: "🆕", ph: "Incidencias detectadas hoy…", area: true },
  { key: "incidencias_resueltas", label: "Incidencias resueltas", emoji: "✅", ph: "Incidencias cerradas hoy…", area: true },
  { key: "incidencias_pendientes", label: "Incidencias pendientes", emoji: "⏳", ph: "Quedan sin resolver…", area: true },
  { key: "asistentas_notas", label: "Asistentas (incidencias / ausencias / comentarios)", emoji: "🧑‍🔧", ph: "Ausencias, incidencias o comentarios de las asistentas…", area: true },
  { key: "residentes_sensibles", label: "Residentes sensibles", emoji: "💗", ph: "Residentes que requieren atención especial…", area: true },
  { key: "familias_contactadas", label: "Familias contactadas", emoji: "👨‍👩‍👧", ph: "Familias con las que has hablado hoy…", area: true },
  { key: "compras_realizadas", label: "Compras realizadas", emoji: "🛒", ph: "Resumen de compras del día…", area: true },
  { key: "desviaciones_presupuesto", label: "Desviaciones de presupuesto", emoji: "💶", ph: "Sobrecostes o ahorros respecto a lo previsto…", area: true },
  { key: "prioridad_manana", label: "Prioridad para mañana", emoji: "🎯", ph: "Lo más importante a resolver mañana…", area: true },
];

const hoyISO = () => new Date().toISOString().slice(0, 10);

const ReporteDiarioCoordinadora = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const usuario = user?.user || user || "";

  const [fecha, setFecha] = useState(hoyISO());
  const [form, setForm] = useState({ ruta_completada: "", necesito_ayuda: [] });
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState(null); // { tipo, texto }
  const [enviado, setEnviado] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await axios.get(`/api/reportes-diarios/coordinadora?fecha=${fecha}`);
      const r = res.data?.reporte;
      if (r) {
        setForm({
          ...r,
          ruta_completada: r.ruta_completada || "",
          necesito_ayuda: Array.isArray(r.necesito_ayuda) ? r.necesito_ayuda : [],
        });
        setEnviado(!!r.enviado);
      } else {
        setForm({ ruta_completada: "", necesito_ayuda: [] });
        setEnviado(false);
      }
    } catch (_) {
      setMsg({ tipo: "error", texto: "No se pudo cargar el reporte." });
    } finally {
      setCargando(false);
    }
  }, [fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAyuda = (k) =>
    setForm((f) => {
      const cur = f.necesito_ayuda || [];
      return { ...f, necesito_ayuda: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] };
    });

  const guardar = async (enviar) => {
    setGuardando(true);
    setMsg(null);
    try {
      await axios.post("/api/reportes-diarios/coordinadora", {
        ...form,
        fecha,
        enviado: enviar,
      });
      setEnviado(enviar);
      setMsg({
        tipo: "ok",
        texto: enviar ? "✅ Reporte enviado correctamente." : "💾 Borrador guardado.",
      });
    } catch (_) {
      setMsg({ tipo: "error", texto: "❌ Error al guardar el reporte." });
    } finally {
      setGuardando(false);
    }
  };

  const esHoy = fecha === hoyISO();
  const tarde = useMemo(() => {
    if (!esHoy) return false;
    const now = new Date();
    const [h, m] = HORA_LIMITE.split(":").map(Number);
    return now.getHours() > h || (now.getHours() === h && now.getMinutes() > m);
  }, [esHoy]);

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <button onClick={() => navigate("/supervisora")} style={S.btnVolver}>← Volver</button>
        <input type="date" value={fecha} max={hoyISO()} onChange={(e) => setFecha(e.target.value)} style={S.date} />
      </div>

      <div style={S.card}>
        <h1 style={S.title}>📋 Reporte diario — <strong>{usuario}</strong></h1>
        <div style={S.subline}>
          <span>Coordinadora</span>
          <span style={{ ...S.badge, ...(enviado ? S.badgeOk : S.badgeDraft) }}>
            {enviado ? "✔ Enviado" : "✎ Borrador"}
          </span>
        </div>
        <div style={{ ...S.deadline, ...(tarde && !enviado ? S.deadlineLate : {}) }}>
          ⏰ Entrega obligatoria antes de las <strong>{HORA_LIMITE}</strong>
          {tarde && !enviado && " — ¡fuera de plazo!"}
        </div>

        {cargando ? (
          <div style={S.loading}>Cargando…</div>
        ) : (
          <>
            {/* Ruta completada */}
            <div style={S.field}>
              <label style={S.label}>🚗 Ruta completada</label>
              <div style={S.segGroup}>
                {[
                  { v: "si", t: "Sí", c: "#16a34a" },
                  { v: "parcial", t: "Parcial", c: "#d97706" },
                  { v: "no", t: "No", c: "#dc2626" },
                ].map((o) => (
                  <button
                    key={o.v}
                    onClick={() => set("ruta_completada", o.v)}
                    style={{
                      ...S.segBtn,
                      ...(form.ruta_completada === o.v
                        ? { background: o.c, color: "#fff", borderColor: o.c }
                        : {}),
                    }}
                  >
                    {o.t}
                  </button>
                ))}
              </div>
            </div>

            {/* Campos de texto */}
            {CAMPOS.map((c) => (
              <div key={c.key} style={S.field}>
                <label style={S.label}>{c.emoji} {c.label}</label>
                <textarea
                  value={form[c.key] || ""}
                  onChange={(e) => set(c.key, e.target.value)}
                  placeholder={c.ph}
                  style={S.textarea}
                  rows={2}
                />
              </div>
            ))}

            {/* Necesito ayuda de */}
            <div style={S.field}>
              <label style={S.label}>🆘 Necesito ayuda de</label>
              <div style={S.chips}>
                {AYUDA_OPCIONES.map((o) => {
                  const on = (form.necesito_ayuda || []).includes(o.key);
                  return (
                    <button
                      key={o.key}
                      onClick={() => toggleAyuda(o.key)}
                      style={{ ...S.chip, ...(on ? S.chipOn : {}) }}
                    >
                      {o.emoji} {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {msg && (
              <div style={{ ...S.msg, ...(msg.tipo === "ok" ? S.msgOk : S.msgErr) }}>
                {msg.texto}
              </div>
            )}

            <div style={S.footer}>
              <button onClick={() => guardar(false)} disabled={guardando} style={S.btnDraft}>
                💾 Guardar borrador
              </button>
              <button onClick={() => guardar(true)} disabled={guardando} style={S.btnSend}>
                📤 Enviar reporte
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const S = {
  page: { minHeight: "100vh", background: "#f1f5f9", padding: "16px", fontFamily: "'Segoe UI', Tahoma, sans-serif" },
  topbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, maxWidth: 780, margin: "0 auto 14px" },
  btnVolver: { padding: "10px 22px", borderRadius: 20, background: "linear-gradient(135deg, #3b82f6, #1d4ed8)", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 15 },
  date: { padding: "9px 12px", borderRadius: 12, border: "2px solid #cbd5e1", fontSize: 15, background: "#fff" },
  card: { maxWidth: 780, margin: "0 auto", background: "#fff", borderRadius: 20, padding: "26px 28px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)" },
  title: { margin: "0 0 6px", fontSize: 26, color: "#0f172a" },
  subline: { display: "flex", alignItems: "center", gap: 10, color: "#64748b", fontSize: 15, marginBottom: 12 },
  badge: { padding: "3px 12px", borderRadius: 20, fontSize: 13, fontWeight: 800 },
  badgeOk: { background: "#dcfce7", color: "#15803d" },
  badgeDraft: { background: "#fef3c7", color: "#b45309" },
  deadline: { background: "#eff6ff", color: "#1e40af", borderRadius: 12, padding: "10px 14px", fontSize: 14, marginBottom: 20, fontWeight: 600 },
  deadlineLate: { background: "#fef2f2", color: "#b91c1c" },
  loading: { textAlign: "center", color: "#64748b", padding: 40 },
  field: { marginBottom: 18 },
  label: { display: "block", fontWeight: 700, color: "#334155", marginBottom: 7, fontSize: 15 },
  textarea: { width: "100%", boxSizing: "border-box", padding: "11px 13px", borderRadius: 12, border: "2px solid #e2e8f0", fontSize: 15, resize: "vertical", fontFamily: "inherit" },
  segGroup: { display: "flex", gap: 10 },
  segBtn: { flex: 1, padding: "11px", borderRadius: 12, border: "2px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 700, fontSize: 15, cursor: "pointer" },
  chips: { display: "flex", flexWrap: "wrap", gap: 10 },
  chip: { padding: "10px 16px", borderRadius: 20, border: "2px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 700, fontSize: 14, cursor: "pointer" },
  chipOn: { background: "#6366f1", color: "#fff", borderColor: "#6366f1" },
  msg: { padding: "12px 16px", borderRadius: 12, fontWeight: 700, marginBottom: 16, textAlign: "center" },
  msgOk: { background: "#dcfce7", color: "#15803d" },
  msgErr: { background: "#fee2e2", color: "#b91c1c" },
  footer: { display: "flex", gap: 14, justifyContent: "flex-end", flexWrap: "wrap" },
  btnDraft: { padding: "14px 24px", borderRadius: 14, border: "2px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 800, fontSize: 16, cursor: "pointer" },
  btnSend: { padding: "14px 30px", borderRadius: 14, border: "none", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer", boxShadow: "0 8px 20px rgba(16,185,129,0.4)" },
};

export default ReporteDiarioCoordinadora;
